import { useNavigate } from 'react-router-dom';

import { appActions } from '../store/appActions';
import { useAuthStore } from '../store/useAuthStore';
import { useWorkspaceStore } from '../store/useWorkspaceStore';
import { useToken } from '../store/useTokenStorage';
import { queryClient } from './query';

import { AuthContext } from '../../shared/contexts/authContext';
import { apiSignUp, apiSignIn, apiSignOut } from '../../shared/api/authFetch';
import { invalidateFileLinkedRowsCache } from '../../features/datasets/hooks/useFileLinkedRows';
import { invalidateRowLinkedFilesCache } from '../../features/datasets/hooks/useRowLinkedFiles';
import type { SignInCredential, SignUpCredential, AuthResult, Auth } from '../../shared/types/AuthType';
import type { CommonType } from '../../shared/types/CommonType';

export const AuthProvider = ({ children }: CommonType) => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const rootFolderId = useWorkspaceStore((state) => state.currentFolderId);
  const setUser = appActions.setUser;
  const reset = appActions.reset;
  const { token, setToken, clearToken } = useToken();
    
  const redirect = (path?: string) => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const redirectUrl = params.get("redirectUrl");
     
    navigate(path || redirectUrl || '/');
  };

  const handleSignOut = () => {
    clearToken();
    reset();
    queryClient.clear();
    invalidateFileLinkedRowsCache();
    invalidateRowLinkedFilesCache();
    navigate('/sign-in');
  };

  const signUp = async (credentials: SignUpCredential): Promise<AuthResult> => {
    try {
      const register = await apiSignUp(credentials);
    
      if (!register.success) {
        throw new Error(register.message || 'Registration failed');
      }
      
      return await signIn({
        email: credentials.email,
        password: credentials.password,
      });

    } catch (error: any) {
      const errorMessage = error.message || 'Registration failed. Please try again.';
      return {
        status: 'failed',
        message: errorMessage,
      };
    }
  };

  const signIn = async (credentials: SignInCredential): Promise<AuthResult> => {
    try {
      const response = await apiSignIn(credentials);

      setToken(response.accessToken);
      setUser(response.user);
      redirect();
      
      return {
        status: 'success',
        message: 'Login successful',
        data: { user: response.user },
      };
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed. Please check your credentials.';
      return {
        status: 'failed',
        message: errorMessage,
      };
    } 
  };

  const signOut = async () => {
    try {
      await apiSignOut();     
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      handleSignOut();
    }
  };

  const updateUser = (userData: Partial<typeof user>) => {
    const currentUser = useAuthStore.getState().user;
    setUser({
      ...currentUser,
      ...userData,
    });
  };

  const value: Auth = {
    authenticated: !!(user?.id && token), //true,
    user,
    rootFolderId,
    signIn,
    signUp,
    signOut,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};